import { collection, getDocs, addDoc, doc, getDoc } from 'firebase/firestore';
import { db, auth } from '../../../../../firebase/config';

export type Location = {
    id: string;
    name: string;
};

export type Bank = {
    id: string;
    name?: string;
};

export type Manager = {
    id: string;
    name: string;
    email?: string;
    mobile?: string;
    type?: string;
};

export type BranchFormData = {
    branchCode: string;
    address: string;
    mobile: string;
    locationId: string;
    securityCode: string;
    psoId?: string;
    branchManagerId?: string;
    operationalManagerId?: string;
    areaManagerId?: string;
    customLocationName?: string;
};

export async function fetchLocations(): Promise<Location[]> {
    const locSnap = await getDocs(collection(db, 'locations'));
    return locSnap.docs.map(d => ({ id: d.id, ...d.data() } as Location));
}

export async function fetchBankInfo(uid: string): Promise<Bank | null> {
    const bankDoc = await getDoc(doc(db, 'banks', uid));
    if (bankDoc.exists()) {
        const bankData = bankDoc.data();
        return {
            id: uid,
            name: bankData.companyName || bankData.name || 'My Bank'
        };
    }
    return {
        id: uid,
        name: 'My Bank'
    };
}

export async function fetchManagers(bankId: string): Promise<Manager[]> {
    const mgrSnap = await getDocs(collection(db, 'banks', bankId, 'managers'));
    return mgrSnap.docs.map(d => ({
        id: d.id,
        ...d.data()
    } as Manager));
}

export async function submitBranch(bankId: string, formData: BranchFormData): Promise<void> {
    const uid = auth.currentUser?.uid;
    if (!uid || !bankId) {
        throw new Error('User not authenticated or bank ID missing');
    }

    let finalLocationId = formData.locationId;

    // Handle custom location
    if (formData.locationId === 'custom' && formData.customLocationName) {
        const locRef = await addDoc(collection(db, 'locations'), {
            name: formData.customLocationName.trim()
        });
        finalLocationId = locRef.id;
    }

    const branchData: any = {
        branchCode: formData.branchCode.trim(),
        address: formData.address.trim(),
        mobile: formData.mobile.trim(),
        locationId: finalLocationId,
        securityCode: formData.securityCode.trim(),
        psoId: formData.psoId || uid, // Use selected psoId or fallback to current user
        isActive: true,
        createdAt: new Date(),
    };

    // Add manager IDs if selected
    if (formData.branchManagerId) {
        branchData.branchManagerId = formData.branchManagerId;
    }
    if (formData.operationalManagerId) {
        branchData.operationalManagerId = formData.operationalManagerId;
    }
    if (formData.areaManagerId) {
        branchData.areaManagerId = formData.areaManagerId;
    }

    await addDoc(collection(db, 'banks', bankId, 'branch'), branchData);
}

